import { Alert, Box, Button, Container, Link, Typography } from "@mui/material";
import React, { useState } from "react";
import { useAuth } from "../allauth/auth";
import ApiClient from "../config/ApiClient";

const SUPPORT_EMAIL = "support@strongmindstrongperformance.com";
const MAILTO_LINK = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Data Download Request")}`;

const AccountDownload: React.FC = () => {
    const me = useAuth()?.data?.user;
    const isLoggedIn = Boolean(me?.id);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [alreadyPending, setAlreadyPending] = useState(false);
    const [submitted, setSubmitted] = useState(false);

    async function handleRequest() {
        if (!isLoggedIn) return;
        setError(null);
        setAlreadyPending(false);
        setSubmitting(true);
        try {
            const res = await ApiClient.post<{ detail?: string }>("/api/account/download/", {});
            if (res.success) {
                setSubmitted(true);
                return;
            }
            const message = res.error ?? "Unable to submit your request. Please try again.";
            if (message.toLowerCase().includes("already pending")) {
                setAlreadyPending(true);
            } else {
                setError(message);
            }
        } catch {
            setError("Unable to submit your request. Please try again later.");
        } finally {
            setSubmitting(false);
        }
    }

    return (
        <Container maxWidth="sm" sx={{ py: 6 }}>
            <Typography variant="h4" component="h1" sx={{ mb: 3, fontWeight: 600 }}>
                Download Your Data
            </Typography>

            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                You can request a copy of the personal data we hold about you on the Strong Mind Strong
                Performance (SMSP) platform.
            </Typography>

            <Box component="section" sx={{ mb: 4 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    What you can request
                </Typography>
                <Typography variant="body1" component="ul" sx={{ pl: 2.5, m: 0 }}>
                    <li>Account profile information</li>
                    <li>Assessment responses and program progress</li>
                    <li>Coach feedback and lesson plan activity</li>
                    <li>Survey responses and check-in records</li>
                    <li>Other personal data associated with your account</li>
                </Typography>
            </Box>

            <Box component="section" sx={{ mb: 4 }}>
                <Typography variant="h6" component="h2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    How to request
                </Typography>

                {submitted ? (
                    <Alert severity="success" data-attr="account-download_submitted-alert">
                        Your request has been submitted. We sent a confirmation to your email address and
                        will respond within 30 days.
                    </Alert>
                ) : (
                    <>
                        {alreadyPending && (
                            <Alert severity="info" sx={{ mb: 2 }}>
                                A data download request is already pending for this account. If you need
                                help, contact <Link href={MAILTO_LINK}>{SUPPORT_EMAIL}</Link>.
                            </Alert>
                        )}

                        {error && (
                            <Alert severity="error" sx={{ mb: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {isLoggedIn ? (
                            <>
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Submit your request below and we will email you a confirmation. Let us
                                    know at <Link href={MAILTO_LINK}>{SUPPORT_EMAIL}</Link> if you need data
                                    from a specific organization.
                                </Typography>
                                <Button
                                    variant="contained"
                                    disabled={submitting}
                                    onClick={handleRequest}
                                    sx={{ textTransform: "none" }}
                                    data-attr="account-download_submit-button"
                                >
                                    {submitting ? "Submitting…" : "Request my data"}
                                </Button>
                            </>
                        ) : (
                            <>
                                {/* Signed-out visitors can still exercise the right, by email. */}
                                <Typography variant="body1" sx={{ mb: 2 }}>
                                    Sign in to submit a request, or email us at{" "}
                                    <Link href={MAILTO_LINK}>{SUPPORT_EMAIL}</Link> from the email address
                                    linked to your SMSP account. Include &ldquo;Data Download
                                    Request&rdquo; in the subject line.
                                </Typography>
                                <Button
                                    component="a"
                                    href={MAILTO_LINK}
                                    variant="contained"
                                    sx={{ textTransform: "none" }}
                                    data-attr="account-download_email-button"
                                >
                                    Email {SUPPORT_EMAIL}
                                </Button>
                            </>
                        )}
                    </>
                )}
            </Box>

            <Box component="section">
                <Typography variant="h6" component="h2" sx={{ mb: 1.5, fontWeight: 600 }}>
                    Response timeline
                </Typography>
                <Typography variant="body1" color="text.secondary">
                    We aim to respond to data download requests within 30 days. We may ask you to verify
                    your identity before providing your data.
                </Typography>
            </Box>
        </Container>
    );
};

export default AccountDownload;
